import { dayjsLondon } from 'lib/dayjs'
import { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { P, Wrapper } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'

import LeadEventIcon from 'components/lead-event/icon'
import { EightxEightEventAttributes } from 'lib/models/eight-x-eight-event'
import { formatToHuman } from 'utils/helpers'

import EightxEightIcon from './eight-x-eight-icon'

const EIGHT_X_EIGHT_QM_BASE_URL = 'https://qm-eu-west.8x8.com/#/search'
const EIGHT_X_EIGHT_QM_IDENTIFIER = 'dignityplc'

const EventWrapper = styled(Wrapper)`
  display: flex;
  margin-bottom: ${GTR.L};
`

const EventSummary = styled(P)`
  font-size: ${FONT.SIZE.S};
  margin-bottom: 0;
  text-decoration: none;
`

const EventListItem = styled.li`
  font-size: ${FONT.SIZE.S};

  &:not(:last-of-type) {
    margin-bottom: ${GTR.XS};
  }
`

const StyledGreyText = styled.span`
  font-size: ${FONT.SIZE.S};
  color: ${COLOR.GREY.MEDIUM};
`

const StyledList = styled.ul`
  margin-top: ${GTR.XS};
`

const get8x8AdminUrl = (guid: string): string => {
  const params = new URLSearchParams({
    guid,
    identifier: EIGHT_X_EIGHT_QM_IDENTIFIER,
  })

  return `${EIGHT_X_EIGHT_QM_BASE_URL}?${params.toString()}`
}

const isAnswered = (event: EightxEightEventAttributes): boolean => {
  return !event.interactionLabels.some((label) =>
    label.toLowerCase().includes('abandoned')
  )
}

const formatEventSummary = ({
  event,
}: {
  event: EightxEightEventAttributes
}): ReactElement => {
  return <span>{formatToHuman(event.direction)} call</span>
}

const formatLeadLink = ({
  leadId,
}: {
  leadId?: number
}): ReactElement | null => {
  if (!leadId) {
    return null
  }

  return (
    <StyledGreyText>
      &bull; <Link to={`/leads/${leadId}`}>View lead</Link>
    </StyledGreyText>
  )
}

const isInboundDirection = (direction: string): boolean =>
  direction === 'inbound'

const isOutboundDirection = (direction: string): boolean =>
  direction === 'outbound'

const getCallAgentLabel = (direction: string): 'to' | 'from' | null => {
  if (isInboundDirection(direction)) return 'to'
  if (isOutboundDirection(direction)) return 'from'

  return null
}

const getLeadEventIconDirection = (
  direction: string
): 'inbound' | 'outbound' =>
  isOutboundDirection(direction) ? 'outbound' : 'inbound'

const formatEventNotes = ({
  event,
}: {
  event: EightxEightEventAttributes
}): ReactElement => {
  const callAgentLabel = getCallAgentLabel(event.direction)

  return (
    <StyledList>
      {event.agentName && callAgentLabel && (
        <EventListItem>
          <strong>Call {callAgentLabel}</strong> {event.agentName}
        </EventListItem>
      )}
      {event.interactionLabel && (
        <EventListItem>
          <strong>Outcome</strong> {formatToHuman(event.interactionLabel)}
        </EventListItem>
      )}
    </StyledList>
  )
}

const EightxEightEvent = ({
  event,
  leadId,
}: {
  event: EightxEightEventAttributes
  leadId?: number
}): ReactElement => {
  const formattedTime = dayjsLondon(event.createdAt).format('D MMM YYYY, HH:mm')

  return (
    <EventWrapper>
      <LeadEventIcon
        direction={getLeadEventIconDirection(event.direction)}
        isAnswered={isAnswered(event)}
        Logo={EightxEightIcon}
      />
      <Wrapper>
        <EventSummary>{formatEventSummary({ event })}</EventSummary>
        <StyledGreyText>{formattedTime}</StyledGreyText>{' '}
        {formatLeadLink({ leadId })}
        {formatEventNotes({ event })}
        {event.guid && (
          <P size="S">
            <a
              href={get8x8AdminUrl(event.guid)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Go to 8x8 recording
            </a>
          </P>
        )}
      </Wrapper>
    </EventWrapper>
  )
}

export default EightxEightEvent
